import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

const EXPOSURE_JOBS = [
  {
    title: 'Marketing Director',
    score: 74,
    tasks: [
      { name: 'Campaign copywriting', score: 91 },
      { name: 'Performance reporting', score: 85 },
      { name: 'A/B test analysis', score: 78 },
      { name: 'Brand strategy', score: 42 },
      { name: 'Stakeholder management', score: 28 },
    ],
    insight:
      'Content creation is nearly fully automatable. Brand intuition and stakeholder influence remain your edge — double down there.',
  },
  {
    title: 'Software Engineer',
    score: 62,
    tasks: [
      { name: 'Code generation', score: 88 },
      { name: 'Documentation', score: 82 },
      { name: 'Code review', score: 58 },
      { name: 'Debugging', score: 45 },
      { name: 'Architecture design', score: 32 },
    ],
    insight:
      'Code generation is nearly automated. Architecture and complex debugging remain your moat — invest there before it narrows.',
  },
  {
    title: 'Financial Analyst',
    score: 67,
    tasks: [
      { name: 'Data gathering', score: 92 },
      { name: 'Report formatting', score: 88 },
      { name: 'Variance analysis', score: 65 },
      { name: 'Financial modeling', score: 48 },
      { name: 'Strategic advising', score: 22 },
    ],
    insight:
      'Routine analysis is disappearing fast. The judgment calls that require business context are where your value compounds.',
  },
  {
    title: 'HR Specialist',
    score: 71,
    tasks: [
      { name: 'Resume screening', score: 94 },
      { name: 'Policy documentation', score: 86 },
      { name: 'Benefits admin', score: 79 },
      { name: 'Employee relations', score: 38 },
      { name: 'Culture building', score: 25 },
    ],
    insight:
      'Administrative HR is being automated rapidly. Relationship-driven work and culture shaping are your defensible skills.',
  },
  {
    title: 'Sales Manager',
    score: 45,
    tasks: [
      { name: 'Lead scoring', score: 82 },
      { name: 'CRM updates', score: 76 },
      { name: 'Pipeline forecasting', score: 55 },
      { name: 'Negotiation', score: 18 },
      { name: 'Team coaching', score: 15 },
    ],
    insight:
      'AI handles the data side well. The human persuasion and team leadership skills keep you ahead of automation.',
  },
  {
    title: 'Product Manager',
    score: 58,
    tasks: [
      { name: 'Competitive research', score: 84 },
      { name: 'User story writing', score: 72 },
      { name: 'Roadmap planning', score: 52 },
      { name: 'Stakeholder alignment', score: 35 },
      { name: 'Vision setting', score: 21 },
    ],
    insight:
      'Research and documentation are increasingly AI-powered. Strategic vision and cross-team influence remain uniquely human.',
  },
]

const STEPS = [
  {
    num: '01',
    titleKey: 'pages.landing.howItWorks.step1.title',
    titleDefault: 'Tell us what you do',
    descKey: 'pages.landing.howItWorks.step1.desc',
    descDefault:
      "Not your job title. Your actual tasks — the work you do every day. We break it down at the task level because that's where AI actually operates.",
    icon: (
      <svg
        className="text-accent h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
      </svg>
    ),
  },
  {
    num: '02',
    titleKey: 'pages.landing.howItWorks.step2.title',
    titleDefault: 'We analyze your exposure',
    descKey: 'pages.landing.howItWorks.step2.desc',
    descDefault:
      "Cross-referencing your tasks against real AI capability data. Which tasks AI can do today. Which ones it's learning. Which ones are safe.",
    icon: (
      <svg
        className="text-accent h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5" />
      </svg>
    ),
  },
  {
    num: '03',
    titleKey: 'pages.landing.howItWorks.step3.title',
    titleDefault: 'See your number',
    descKey: 'pages.landing.howItWorks.step3.desc',
    descDefault:
      'Your personal AI exposure score. Task-by-task breakdown. How you compare. Not a personality quiz — a career intelligence report.',
    icon: (
      <svg
        className="text-accent h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
      </svg>
    ),
  },
]

const FEATURES = [
  {
    titleKey: 'pages.landing.features.taskAnalysis.title',
    titleDefault: 'Task-Level Analysis',
    descKey: 'pages.landing.features.taskAnalysis.desc',
    descDefault:
      "We don't assess job titles — we analyze the 12-20 individual tasks that make up your role. Because AI replaces tasks, not people.",
  },
  {
    titleKey: 'pages.landing.features.realData.title',
    titleDefault: 'Real Capability Data',
    descKey: 'pages.landing.features.realData.desc',
    descDefault:
      "Cross-referenced against actual AI benchmarks, not opinion surveys. What AI can do today, what it's learning, what remains human.",
  },
  {
    titleKey: 'pages.landing.features.exposureScore.title',
    titleDefault: 'Personal Exposure Score',
    descKey: 'pages.landing.features.exposureScore.desc',
    descDefault:
      'A single number (0-100) that tells you exactly where you stand. Updated as AI capabilities change.',
  },
  {
    titleKey: 'pages.landing.features.taskBreakdown.title',
    titleDefault: 'Task Breakdown',
    descKey: 'pages.landing.features.taskBreakdown.desc',
    descDefault:
      'See which of your daily tasks are most at risk and which give you defensive advantage.',
  },
  {
    titleKey: 'pages.landing.features.peerComparison.title',
    titleDefault: 'Peer Comparison',
    descKey: 'pages.landing.features.peerComparison.desc',
    descDefault:
      'How your exposure compares to others in your field. Understand where you rank relative to your peers.',
  },
  {
    titleKey: 'pages.landing.features.actionable.title',
    titleDefault: 'Actionable Intelligence',
    descKey: 'pages.landing.features.actionable.desc',
    descDefault:
      'Not just a score — specific guidance on which skills to develop and which tasks to prioritize.',
  },
]

const FAQS = [
  {
    questionKey: 'pages.landing.faq.q1.question',
    questionDefault: 'How is my exposure score calculated?',
    answerKey: 'pages.landing.faq.q1.answer',
    answerDefault:
      'We analyze each task in your role against current AI capability benchmarks. Each task receives an individual exposure rating based on how well AI can perform it today, and these are weighted by importance to produce your overall score.',
  },
  {
    questionKey: 'pages.landing.faq.q2.question',
    questionDefault: 'Is my data private?',
    answerKey: 'pages.landing.faq.q2.answer',
    answerDefault:
      "Completely. We don't store personally identifiable information. Your assessment is anonymous and your results are only accessible via your unique share link.",
  },
  {
    questionKey: 'pages.landing.faq.q3.question',
    questionDefault: 'How long does it take?',
    answerKey: 'pages.landing.faq.q3.answer',
    answerDefault:
      "About 3 minutes. You'll answer 25 questions about your role, daily tasks, and how you currently use AI tools. No account required.",
  },
  {
    questionKey: 'pages.landing.faq.q4.question',
    questionDefault: 'What do the scores mean?',
    answerKey: 'pages.landing.faq.q4.answer',
    answerDefault:
      '0-19 means minimal exposure (blue). 20-39 is low (green). 40-59 is moderate (amber). 60-79 is high (orange). 80-100 is critical (red). Your score tells you how much of your work AI can currently replicate.',
  },
]

function ExposureScanCard() {
  const [visibleCount, setVisibleCount] = useState(0)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [taskBarProgress, setTaskBarProgress] = useState(0)
  const cardRef = useRef<HTMLDivElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const el = cardRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true

          EXPOSURE_JOBS.forEach((_, i) => {
            setTimeout(() => setVisibleCount(i + 1), i * 250)
          })

          const totalRevealTime = EXPOSURE_JOBS.length * 250 + 600
          setTimeout(() => {
            setExpandedIndex(0)
            setTaskBarProgress(0)
            requestAnimationFrame(() => setTaskBarProgress(1))
          }, totalRevealTime)
        }
      },
      { threshold: 0.2 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (expandedIndex === null) return

    const timer = setTimeout(() => {
      const next = (expandedIndex + 1) % EXPOSURE_JOBS.length
      setTaskBarProgress(0)
      setExpandedIndex(next)
      requestAnimationFrame(() => setTaskBarProgress(1))
    }, 4000)

    return () => clearTimeout(timer)
  }, [expandedIndex])

  return (
    <div ref={cardRef}>
      <span className="text-accent flex items-center gap-2 text-xs font-semibold tracking-widest uppercase">
        <span className="bg-accent h-2 w-2" />
        Exposure Scan
      </span>

      <div className="mt-4 space-y-3">
        {EXPOSURE_JOBS.map((job, i) => {
          const isVisible = i < visibleCount
          const isExpanded = expandedIndex === i

          return (
            <div
              key={job.title}
              onClick={() => {
                setTaskBarProgress(0)
                setExpandedIndex(i)
                requestAnimationFrame(() => setTaskBarProgress(1))
              }}
              className={`cursor-pointer overflow-hidden rounded-xl bg-[#1a1a1a] transition-all duration-500 ${
                isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-4 opacity-0'
              }`}
            >
              <div className="relative px-5 pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="bg-accent h-2.5 w-2.5" />
                    <span className="text-base font-semibold text-white">
                      {job.title}
                    </span>
                  </div>
                  <span className="text-accent/80 font-serif text-3xl font-bold italic tabular-nums">
                    {isVisible ? job.score : 0}
                  </span>
                </div>
                <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-[#2c2c2c]">
                  <div
                    className="from-accent/60 to-accent h-full rounded-full bg-gradient-to-r transition-all duration-1000"
                    style={{
                      width: isVisible ? `${job.score}%` : '0%',
                    }}
                  />
                </div>
              </div>

              <div
                className="transition-all duration-500 ease-in-out"
                style={{
                  maxHeight: isExpanded ? '400px' : '0px',
                  opacity: isExpanded ? 1 : 0,
                }}
              >
                <div className="border-t border-[#2c2c2c] px-5 pt-4 pb-5">
                  <span className="text-accent text-xs font-semibold tracking-widest uppercase">
                    Task Exposure
                  </span>

                  <div className="mt-4 space-y-4">
                    {job.tasks.map((task, ti) => (
                      <div
                        key={task.name}
                        className="grid grid-cols-[1fr_auto] items-center gap-3"
                        style={{
                          opacity: isExpanded ? 1 : 0,
                          transform: isExpanded
                            ? 'translateX(0)'
                            : 'translateX(-8px)',
                          transition: `all 400ms ease ${ti * 80}ms`,
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span className="min-w-0 shrink-0 text-sm text-[#ccc]">
                            {task.name}
                          </span>
                          <div className="h-[3px] w-full overflow-hidden rounded-full bg-[#2c2c2c]">
                            <div
                              className="from-accent/50 to-accent h-full rounded-full bg-gradient-to-r"
                              style={{
                                width: `${task.score * taskBarProgress}%`,
                                transition: 'width 800ms ease-out',
                              }}
                            />
                          </div>
                        </div>
                        <span className="text-accent text-sm font-semibold tabular-nums">
                          {Math.round(task.score * taskBarProgress)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 border-t border-[#2c2c2c] pt-4">
                    <span className="text-accent text-xs font-semibold tracking-widest uppercase">
                      Key Insight
                    </span>
                    <p className="mt-2 text-sm leading-relaxed text-[#999]">
                      {job.insight}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function AcriLandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  return (
    <div className="bg-app-bg text-primary min-h-screen font-sans">
      {/* Header */}
      <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2">
          <span className="bg-accent h-6 w-6 rounded-full" />
          <span className="font-serif text-xl font-bold tracking-tight">
            ACRI
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/account/sign-in"
            className="text-muted hover:text-primary text-sm font-medium no-underline transition-colors"
          >
            {t('buttons.login', { defaultValue: 'Log In' })}
          </Link>
          <button
            onClick={() => navigate('/onboarding')}
            className="bg-accent text-primary cursor-pointer rounded-lg px-5 py-2.5 text-sm font-semibold transition hover:-translate-y-0.5"
          >
            {t('pages.landing.header.cta', {
              defaultValue: 'See Your Score →',
            })}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="grid items-center gap-12 lg:grid-cols-[1.5fr_1fr]">
          {/* Left column */}
          <div>
            <span className="text-accent text-xs font-semibold tracking-widest uppercase">
              {t('pages.landing.hero.label', {
                defaultValue: 'Career Intelligence',
              })}
            </span>
            <h1 className="mt-5 font-serif text-[2.5rem] leading-[1.1] font-bold tracking-tight md:text-[3.25rem]">
              {t('pages.landing.hero.titleLine1', {
                defaultValue: 'Your career has a number.',
              })}
              <br />
              <span className="text-accent italic">
                {t('pages.landing.hero.titleLine2', {
                  defaultValue: "You just can't see it.",
                })}
              </span>
            </h1>
            <p className="text-muted mt-6 max-w-lg text-base leading-relaxed">
              {t('pages.landing.hero.subtitle', {
                defaultValue:
                  "AI is quietly sorting knowledge workers into winners and losers. Your exposure score shows you which side you're on — before your employer decides for you.",
              })}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={() => navigate('/onboarding')}
                className="bg-accent cursor-pointer rounded-lg px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                {t('pages.landing.hero.cta', {
                  defaultValue: 'See Your Score — Free →',
                })}
              </button>
              <a
                href="#how-it-works"
                className="rounded-lg bg-[#1a1a1a] px-6 py-3 text-center text-sm font-semibold text-white no-underline transition hover:bg-[#2c2c2c]"
              >
                {t('pages.landing.hero.secondary', {
                  defaultValue: 'How It Works',
                })}
              </a>
            </div>
            <p className="text-muted mt-6 text-xs">
              {t('pages.landing.hero.badges', { defaultValue: '3 min' })}
            </p>
          </div>

          {/* Right column — Exposure Scan Card */}
          <ExposureScanCard />
        </div>
      </section>

      {/* The Problem */}
      <section className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <div className="grid items-start gap-12 md:grid-cols-[1fr_1.2fr]">
          <div>
            <span className="text-accent text-xs font-semibold tracking-widest uppercase">
              {t('pages.landing.problem.label', {
                defaultValue: 'The Problem',
              })}
            </span>
            <h2 className="mt-4 font-serif text-[1.75rem] leading-tight font-bold tracking-tight md:text-[2.5rem]">
              {t('pages.landing.problem.titleLine1', {
                defaultValue: 'You track everything except the one asset that ',
              })}
              <span className="text-accent italic">
                {t('pages.landing.problem.titleLine2', {
                  defaultValue: 'funds your life.',
                })}
              </span>
            </h2>
          </div>
          <div className="text-muted space-y-5 text-base leading-relaxed">
            <p>
              {t('pages.landing.problem.p1', {
                defaultValue:
                  'You check your credit score. You check your portfolio. You track your steps, your sleep, your calories.',
              })}
            </p>
            <p>
              {t('pages.landing.problem.p2', {
                defaultValue:
                  "But the one number that determines whether you'll still have a paycheck in two years? You're completely in the dark.",
              })}
            </p>
            <p>
              {t('pages.landing.problem.p3', {
                defaultValue:
                  "AI won't take your job. The colleague who figured it out six months before you will.",
              })}
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-[#1a1a1a]">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="text-center">
            <span className="text-accent text-xs font-semibold tracking-widest uppercase">
              {t('pages.landing.howItWorks.label', {
                defaultValue: 'How It Works',
              })}
            </span>
            <h2 className="mt-4 font-serif text-[1.75rem] font-bold tracking-tight text-white md:text-[2.5rem]">
              {t('pages.landing.howItWorks.titleLine1', {
                defaultValue: 'Three minutes to ',
              })}
              <span className="text-accent italic">
                {t('pages.landing.howItWorks.titleLine2', {
                  defaultValue: 'clarity.',
                })}
              </span>
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {STEPS.map((step) => (
              <div
                key={step.num}
                className="relative rounded-xl bg-[#2c2c2c] p-6"
              >
                <span className="absolute top-5 right-5 font-serif text-2xl font-bold text-[#444]">
                  {step.num}
                </span>
                <div className="mb-4">{step.icon}</div>
                <h3 className="text-lg font-semibold text-white">
                  {t(step.titleKey, { defaultValue: step.titleDefault })}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#999]">
                  {t(step.descKey, { defaultValue: step.descDefault })}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="text-center">
            <span className="text-accent text-xs font-semibold tracking-widest uppercase">
              {t('pages.landing.features.label', { defaultValue: 'Features' })}
            </span>
            <h2 className="mt-4 font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
              {t('pages.landing.features.titleLine1', {
                defaultValue: 'Not a quiz. Not a course. ',
              })}
              <span className="text-accent italic">
                {t('pages.landing.features.titleLine2', {
                  defaultValue: 'Career intelligence.',
                })}
              </span>
            </h2>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feat) => (
              <div
                key={feat.titleKey}
                className="border-border rounded-xl border bg-white p-6"
              >
                <h3 className="text-primary text-base font-semibold">
                  {t(feat.titleKey, { defaultValue: feat.titleDefault })}
                </h3>
                <p className="text-muted mt-2 text-sm leading-relaxed">
                  {t(feat.descKey, { defaultValue: feat.descDefault })}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-2xl px-6 py-20 md:py-28">
          <div className="text-center">
            <span className="text-accent text-xs font-semibold tracking-widest uppercase">
              {t('pages.landing.faq.label', { defaultValue: 'FAQ' })}
            </span>
            <h2 className="mt-4 font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
              {t('pages.landing.faq.title', {
                defaultValue: 'Common questions',
              })}
            </h2>
          </div>
          <div className="divide-border mt-12 divide-y">
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx
              return (
                <div key={idx} className="py-5">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="flex w-full cursor-pointer items-center justify-between text-left"
                  >
                    <span className="text-primary pr-4 text-[0.95rem] font-medium">
                      {t(faq.questionKey, {
                        defaultValue: faq.questionDefault,
                      })}
                    </span>
                    <svg
                      className={`text-accent h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isOpen && (
                    <p className="text-muted mt-3 text-sm leading-relaxed">
                      {t(faq.answerKey, { defaultValue: faq.answerDefault })}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-2xl px-6 pt-20 pb-16 text-center md:pt-28">
          <h2 className="font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
            {t('pages.landing.bottomCta.titleLine1', {
              defaultValue: 'Get started in ',
            })}
            <span className="text-accent italic">
              {t('pages.landing.bottomCta.titleLine2', {
                defaultValue: 'minutes.',
              })}
            </span>
          </h2>
          <p className="text-muted mx-auto mt-4 max-w-lg text-base leading-relaxed">
            {t('pages.landing.bottomCta.subtitle', {
              defaultValue:
                'No account needed. No credit card. Just three minutes and honest answers about what you do every day.',
            })}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              onClick={() => navigate('/onboarding')}
              className="bg-accent cursor-pointer rounded-lg px-8 py-3.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              {t('pages.landing.bottomCta.cta', {
                defaultValue: 'See Your Score',
              })}
            </button>
            <button
              onClick={() => navigate('/employers')}
              className="border-primary text-primary hover:bg-primary cursor-pointer rounded-lg border px-8 py-3.5 text-sm font-semibold transition hover:text-white"
            >
              {t('pages.landing.bottomCta.employers', {
                defaultValue: 'For Employers',
              })}
            </button>
          </div>
        </div>
      </section>

      {/* Footer with landscape */}
      <footer>
        <div className="relative h-72 w-full overflow-hidden md:h-96">
          <img
            src="/images/landscape-hero.png"
            alt="Mountain landscape at golden hour"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="bg-[#1a1a1a] py-8">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6">
            <div className="flex items-center gap-2">
              <span className="bg-accent h-6 w-6 rounded-full" />
              <span className="font-serif text-xl font-bold text-white">
                ACRI
              </span>
            </div>
            <div className="flex items-center gap-x-4 text-sm text-white">
              <Link
                to="https://app-studio.ai/privacy"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.privacy', { defaultValue: 'Privacy' })}
              </Link>
              <span className="text-[#555]">&middot;</span>
              <Link
                to="https://app-studio.ai/terms"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.terms', { defaultValue: 'Terms' })}
              </Link>
              <span className="text-[#555]">&middot;</span>
              <Link
                to="/help-center"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.help', { defaultValue: 'Help Center' })}
              </Link>
              <span className="text-[#555]">&middot;</span>
              <Link
                to="/help-center/contact"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.contact', { defaultValue: 'Contact' })}
              </Link>
              <span className="text-[#555]">&middot;</span>
              <Link
                to="/help-center/cancel-subscription"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.cancelSubscription', {
                  defaultValue: 'How to Cancel Subscription',
                })}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
